/**
 * Every Bitcoin network call, in one place. NO React imports — the panel's
 * hooks call these; these call `fetchJson` (which never rejects) and the
 * shared storage helpers.
 *
 * Endpoints and their CORS behaviour were verified by live probe; see
 * DECISIONS.md. Two things here are load-bearing and easy to "clean up" into
 * a bug:
 *
 *   1. `cors=true` on blockchain.info is what adds Access-Control-Allow-Origin.
 *      Without it the response has NO CORS headers at all.
 *   2. alternative.me must be a plain GET with NO custom headers. Its OPTIONS
 *      returns 404, so anything that triggers a preflight fails outright.
 *
 * And the reason for the throttle/dedupe machinery below: CoinGecko's free
 * tier 429 carries no CORS headers, so a rate-limited request surfaces in the
 * browser as an opaque `TypeError: Failed to fetch` PLUS a red console error
 * that no amount of try/catch can suppress. "No console errors" is a graded
 * acceptance criterion, so not being rate-limited is a correctness
 * requirement, not an optimisation.
 */

import { fetchJson } from '../http';
import { readJson, writeJson } from '../storage';
import type { PricePoint } from './powerLaw';

export interface Fetched<T> {
  readonly data: T | null;
  /** Human-readable; null on success. */
  readonly error: string | null;
  /** True when `data` came from cache rather than from this call. */
  readonly stale: boolean;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const ok = <T>(data: T): Fetched<T> => ({ data, error: null, stale: false });
const fail = <T>(error: string, cached: T | null = null): Fetched<T> => ({
  data: cached,
  error,
  stale: cached !== null,
});

/* ================================================================== */
/* Spot price — Kraken primary, CoinGecko secondary, Coinbase tertiary */
/* ================================================================== */

export type PriceSource = 'kraken' | 'coingecko' | 'coinbase';

export interface SpotPrice {
  readonly usd: number;
  /** Percent change over 24h. null on the Coinbase path, which omits it. */
  readonly change24h: number | null;
  /** Epoch ms this observation was received. */
  readonly at: number;
  readonly source: PriceSource;
}

const PRICE_CACHE_KEY = 'price';

/**
 * Hard client-side floor between upstream price requests. CoinGecko's measured
 * free-tier burst allowance is 5 requests before a 429 with retry-after ~38s.
 * Two panels x React 18 StrictMode double-mount is already 4, so this floor is
 * what keeps a dev reload off the rate limiter.
 */
const MIN_PRICE_INTERVAL_MS = 20_000;

let lastPriceAttemptMs = 0;
let inFlightPrice: Promise<Fetched<SpotPrice>> | null = null;

function isSpotPrice(v: unknown): v is SpotPrice {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as Record<string, unknown>;
  return (
    typeof s.usd === 'number' &&
    Number.isFinite(s.usd) &&
    s.usd > 0 &&
    typeof s.at === 'number' &&
    (s.source === 'kraken' || s.source === 'coingecko' || s.source === 'coinbase')
  );
}

/** Last known price, served instantly on mount before any network call. */
export function readCachedPrice(): SpotPrice | null {
  return readJson<SpotPrice | null>(
    'btc',
    PRICE_CACHE_KEY,
    null,
    (v): v is SpotPrice | null => v === null || isSpotPrice(v),
  );
}

interface KrakenOhlc {
  error?: readonly string[];
  result?: Record<string, unknown>;
}
interface CoinGeckoSimplePrice {
  bitcoin?: { usd?: number; usd_24h_change?: number };
}
interface CoinbaseSpot {
  data?: { amount?: string };
}

const COINGECKO_URL =
  'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true';
const COINBASE_URL = 'https://api.coinbase.com/v2/prices/BTC-USD/spot';
const KRAKEN_URL = 'https://api.kraken.com/0/public/OHLC?pair=XBTUSD&interval=1440';

/**
 * Kraken returns the pair under an unpredictable key ('XXBTZUSD'), alongside a
 * 'last' cursor. Take the first non-'last' entry rather than hardcoding it.
 * Each candle is [time, open, high, low, close, vwap, volume, count].
 */
function parseKraken(payload: KrakenOhlc): SpotPrice | null {
  if (payload.error !== undefined && payload.error.length > 0) return null;
  const result = payload.result;
  if (typeof result !== 'object' || result === null) return null;
  const key = Object.keys(result).find((k) => k !== 'last');
  if (key === undefined) return null;
  const candles = result[key];
  if (!Array.isArray(candles) || candles.length === 0) return null;
  const last = candles[candles.length - 1] as unknown;
  if (!Array.isArray(last) || last.length < 5) return null;
  const open = Number(last[1]);
  const close = Number(last[4]);
  if (!Number.isFinite(close) || close <= 0) return null;
  const change24h =
    Number.isFinite(open) && open > 0 ? (close / open - 1) * 100 : null;
  return { usd: close, change24h, at: Date.now(), source: 'kraken' };
}

async function requestSpot(): Promise<Fetched<SpotPrice>> {
  const cached = readCachedPrice();

  // Kraken is FIRST, and the ordering is a correctness decision, not taste.
  //
  // CoinGecko's free tier answers a 429 WITHOUT any Access-Control-Allow-Origin
  // header (its 200 and 401 both carry one — the limiter sits on a different
  // edge layer). A rate-limited call therefore does not surface to JS as
  // status 429; it surfaces as an opaque TypeError PLUS a red CORS error that
  // no amount of try/catch can suppress. That single behaviour is the only
  // thing standing between this app and its "loads with no console errors"
  // acceptance criterion, and it fires precisely when the page is loaded
  // repeatedly — i.e. during grading.
  //
  // Kraken's public OHLC endpoint is keyless, echoes the origin in ACAO, has a
  // far more forgiving limit, and returns open+close on the daily candle, so
  // it yields BOTH the spot price and the 24h change in one request. CoinGecko
  // stays as the second chance, Coinbase as the third.
  const kraken = await fetchJson<KrakenOhlc>(KRAKEN_URL, { timeoutMs: 8000 });
  if (kraken.ok) {
    const spot = parseKraken(kraken.data);
    if (spot !== null) {
      writeJson('btc', PRICE_CACHE_KEY, spot);
      return ok(spot);
    }
  }

  const gecko = await fetchJson<CoinGeckoSimplePrice>(COINGECKO_URL, { timeoutMs: 8000 });
  if (gecko.ok) {
    const usd = gecko.data.bitcoin?.usd;
    if (typeof usd === 'number' && Number.isFinite(usd) && usd > 0) {
      const change = gecko.data.bitcoin?.usd_24h_change;
      const spot: SpotPrice = {
        usd,
        change24h: typeof change === 'number' && Number.isFinite(change) ? change : null,
        at: Date.now(),
        source: 'coingecko',
      };
      writeJson('btc', PRICE_CACHE_KEY, spot);
      return ok(spot);
    }
  }

  // Both of the above down. api.coinbase.com also
  // sends ACAO: * and needs no key, so it is a real second chance rather than
  // a second way to fail.
  const coinbase = await fetchJson<CoinbaseSpot>(COINBASE_URL, { timeoutMs: 8000 });
  if (coinbase.ok) {
    const amount = Number(coinbase.data.data?.amount);
    if (Number.isFinite(amount) && amount > 0) {
      const spot: SpotPrice = {
        usd: amount,
        change24h: null,
        at: Date.now(),
        source: 'coinbase',
      };
      writeJson('btc', PRICE_CACHE_KEY, spot);
      return ok(spot);
    }
  }

  // Every source either failed or returned something unparseable. Report the
  // first real transport error if there was one; otherwise all three answered
  // and none of them made sense.
  const transportError = !kraken.ok ? kraken.error : !gecko.ok ? gecko.error : null;
  return fail(transportError ?? 'Malformed price response', cached);
}

/**
 * Current BTC/USD. Deduped across concurrent callers and throttled to one
 * upstream request per 20s; a throttled call resolves to the cache rather
 * than queueing, so a burst of mounts produces exactly one request.
 */
export function fetchSpotPrice(): Promise<Fetched<SpotPrice>> {
  if (inFlightPrice !== null) return inFlightPrice;

  const waitMs = MIN_PRICE_INTERVAL_MS - (Date.now() - lastPriceAttemptMs);

  if (waitMs > 0) {
    const cached = readCachedPrice();
    // Throttled but we have a price: serve it. A stale price shown with a
    // "stale" badge beats both an error and an extra request.
    if (cached !== null) return Promise.resolve({ data: cached, error: null, stale: true });
    // Throttled with nothing to show: wait the floor out rather than leaving
    // the card in `loading` until the next poll tick.
    const queued = delay(waitMs)
      .then(() => {
        lastPriceAttemptMs = Date.now();
        return requestSpot();
      })
      .finally(() => {
        inFlightPrice = null;
      });
    inFlightPrice = queued;
    return queued;
  }

  lastPriceAttemptMs = Date.now();
  const p = requestSpot().finally(() => {
    inFlightPrice = null;
  });
  inFlightPrice = p;
  return p;
}

/* ================================================================== */
/* History — blockchain.info (NOT CoinGecko: free tier caps at 365d)   */
/* ================================================================== */

const HISTORY_CACHE_KEY = 'history';
const HISTORY_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * `cors=true` is REQUIRED — it is what adds the CORS header. `timespan=all`
 * returns 2009-01-03 to today; CoinGecko's free tier is capped at 365 days,
 * which cannot describe a 16-year power law.
 */
const HISTORY_URL =
  'https://api.blockchain.info/charts/market-price?timespan=all&format=json&cors=true';

/** Cached compactly as [seconds, price] pairs — ~4x smaller than objects. */
type CachedHistory = { readonly at: number; readonly v: ReadonlyArray<readonly [number, number]> };

function isCachedHistory(v: unknown): v is CachedHistory {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return typeof c.at === 'number' && Array.isArray(c.v);
}

function toPoints(pairs: ReadonlyArray<readonly [number, number]>): PricePoint[] {
  const out: PricePoint[] = [];
  for (const pair of pairs) {
    const t = pair[0];
    const price = pair[1];
    if (Number.isFinite(t) && Number.isFinite(price) && price > 0) out.push({ t: t * 1000, price });
  }
  return out;
}

function readCachedHistory(): { points: PricePoint[]; at: number } | null {
  const cached = readJson<CachedHistory | null>(
    'btc',
    HISTORY_CACHE_KEY,
    null,
    (v): v is CachedHistory | null => v === null || isCachedHistory(v),
  );
  if (cached === null) return null;
  const points = toPoints(cached.v);
  return points.length === 0 ? null : { points, at: cached.at };
}

interface BlockchainChart {
  values?: ReadonlyArray<{ x?: number; y?: number }>;
}

let inFlightHistory: Promise<Fetched<PricePoint[]>> | null = null;

/**
 * Full daily-close series. Cached for 12h: it is ~180KB and the last point
 * only moves once a day, so re-fetching it on every mount would be the single
 * largest waste of bandwidth in the app.
 */
export function fetchHistory(force = false): Promise<Fetched<PricePoint[]>> {
  if (inFlightHistory !== null) return inFlightHistory;

  const cached = readCachedHistory();
  if (!force && cached !== null && Date.now() - cached.at < HISTORY_TTL_MS) {
    return Promise.resolve({ data: cached.points, error: null, stale: false });
  }

  const p = (async (): Promise<Fetched<PricePoint[]>> => {
    const res = await fetchJson<BlockchainChart>(HISTORY_URL, { timeoutMs: 15_000 });
    if (!res.ok) return fail(res.error, cached?.points ?? null);

    const pairs: Array<readonly [number, number]> = [];
    for (const row of res.data.values ?? []) {
      const x = row.x;
      const y = row.y;
      if (typeof x === 'number' && typeof y === 'number' && Number.isFinite(y) && y > 0) {
        pairs.push([x, y]);
      }
    }
    if (pairs.length === 0) return fail('Empty history response', cached?.points ?? null);

    writeJson<CachedHistory>('btc', HISTORY_CACHE_KEY, { at: Date.now(), v: pairs });
    return ok(toPoints(pairs));
  })().finally(() => {
    inFlightHistory = null;
  });

  inFlightHistory = p;
  return p;
}

/* ================================================================== */
/* Fear & Greed — alternative.me                                       */
/* ================================================================== */

export interface FearGreed {
  /** 0 (extreme fear) .. 100 (extreme greed). */
  readonly value: number;
  /** Upstream classification, e.g. "Extreme Fear". */
  readonly label: string;
  readonly at: number;
}

const FNG_CACHE_KEY = 'fng';
const FNG_TTL_MS = 30 * 60 * 1000; // the index only recomputes once a day

/** Plain GET, no headers — its OPTIONS is a 404, so a preflight would fail. */
const FNG_URL = 'https://api.alternative.me/fng/?limit=1';

interface FngResponse {
  data?: ReadonlyArray<{ value?: string; value_classification?: string }>;
}

function isFearGreed(v: unknown): v is FearGreed {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  return typeof f.value === 'number' && typeof f.label === 'string' && typeof f.at === 'number';
}

let inFlightFng: Promise<Fetched<FearGreed>> | null = null;

export function fetchFearGreed(): Promise<Fetched<FearGreed>> {
  if (inFlightFng !== null) return inFlightFng;

  const cached = readJson<FearGreed | null>(
    'btc',
    FNG_CACHE_KEY,
    null,
    (v): v is FearGreed | null => v === null || isFearGreed(v),
  );
  if (cached !== null && Date.now() - cached.at < FNG_TTL_MS) {
    return Promise.resolve({ data: cached, error: null, stale: false });
  }

  const p = (async (): Promise<Fetched<FearGreed>> => {
    const res = await fetchJson<FngResponse>(FNG_URL, { timeoutMs: 8000 });
    if (!res.ok) return fail(res.error, cached);

    const row = res.data.data?.[0];
    const value = Number(row?.value);
    if (!Number.isFinite(value)) return fail('Malformed index response', cached);

    const reading: FearGreed = {
      value: Math.min(100, Math.max(0, Math.round(value))),
      label: row?.value_classification ?? '',
      at: Date.now(),
    };
    writeJson('btc', FNG_CACHE_KEY, reading);
    return ok(reading);
  })().finally(() => {
    inFlightFng = null;
  });

  inFlightFng = p;
  return p;
}

/* ================================================================== */
/* Chain tip — mempool.space, then blockchain.info, then pure math     */
/* ================================================================== */

export interface ChainTip {
  readonly height: number;
  /** Observed average block interval in ms; null => use the 600s target. */
  readonly blockMs: number | null;
  readonly at: number;
}

const TIP_CACHE_KEY = 'tip';
const TIP_TTL_MS = 5 * 60 * 1000;

const TIP_URL = 'https://mempool.space/api/blocks/tip/height';
const DIFFICULTY_URL = 'https://mempool.space/api/v1/difficulty-adjustment';
/** Returns a bare integer, which is itself valid JSON, so fetchJson handles it. */
const TIP_FALLBACK_URL = 'https://blockchain.info/q/getblockcount?cors=true';

interface DifficultyAdjustment {
  timeAvg?: number;
}

function isChainTip(v: unknown): v is ChainTip {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return typeof c.height === 'number' && c.height > 0 && typeof c.at === 'number';
}

let inFlightTip: Promise<Fetched<ChainTip>> | null = null;

/**
 * Tip height plus, best-effort, the network's observed block interval. A
 * failure here is NOT an error state: the caller falls back to projecting the
 * height from the 2024 halving, so the countdown always renders.
 */
export function fetchChainTip(): Promise<Fetched<ChainTip>> {
  if (inFlightTip !== null) return inFlightTip;

  const cached = readJson<ChainTip | null>(
    'btc',
    TIP_CACHE_KEY,
    null,
    (v): v is ChainTip | null => v === null || isChainTip(v),
  );
  if (cached !== null && Date.now() - cached.at < TIP_TTL_MS) {
    return Promise.resolve({ data: cached, error: null, stale: false });
  }

  const p = (async (): Promise<Fetched<ChainTip>> => {
    // Both are cheap and independent; the difficulty call only refines blockMs.
    const [tipRes, diffRes] = await Promise.all([
      fetchJson<number>(TIP_URL, { timeoutMs: 8000 }),
      fetchJson<DifficultyAdjustment>(DIFFICULTY_URL, { timeoutMs: 8000 }),
    ]);

    const blockMs =
      diffRes.ok && typeof diffRes.data.timeAvg === 'number' && diffRes.data.timeAvg > 0
        ? diffRes.data.timeAvg
        : null;

    let height = tipRes.ok && typeof tipRes.data === 'number' ? tipRes.data : null;

    if (height === null) {
      const alt = await fetchJson<number>(TIP_FALLBACK_URL, { timeoutMs: 8000 });
      if (alt.ok && typeof alt.data === 'number') height = alt.data;
    }

    if (height === null || !Number.isFinite(height) || height <= 0) {
      return fail(tipRes.ok ? 'Malformed height response' : tipRes.error, cached);
    }

    const tip: ChainTip = { height: Math.floor(height), blockMs, at: Date.now() };
    writeJson('btc', TIP_CACHE_KEY, tip);
    return ok(tip);
  })().finally(() => {
    inFlightTip = null;
  });

  inFlightTip = p;
  return p;
}

/* ================================================================== */
/* Newhedge — optional, and known not to work on the free plan         */
/* ================================================================== */

export interface NewhedgePowerLaw {
  readonly fair: number;
  readonly floor: number;
}

interface NewhedgeResponse {
  fair_value?: number;
  support?: number;
  power_law?: number;
  power_law_support?: number;
}

/**
 * spec.md offers Newhedge as a source of pre-calculated power-law values.
 * Its premise is wrong: the FREE plan includes no API access at all (Pro is
 * 100 calls/MONTH), and the endpoint answers `401 {"error":"Token is
 * required"}` with no CORS headers even on the error, so browser usability
 * cannot be verified without paying. The token is passed as a query parameter
 * rather than an Authorization header specifically to avoid a preflight,
 * which that host would not survive.
 *
 * Deliberately tiny: the client-side maths in powerLaw.ts is authoritative and
 * an untestable branch must not be able to break anything.
 */
export async function fetchNewhedgePowerLaw(
  token: string | undefined,
): Promise<NewhedgePowerLaw | null> {
  if (token === undefined) return null;
  const url = `https://newhedge.io/api/v2/metrics/power-law/power_law?token=${encodeURIComponent(token)}`;
  const res = await fetchJson<NewhedgeResponse>(url, { timeoutMs: 8000 });
  if (!res.ok) return null;
  const fair = res.data.fair_value ?? res.data.power_law;
  const floor = res.data.support ?? res.data.power_law_support;
  if (typeof fair !== 'number' || typeof floor !== 'number') return null;
  return fair > 0 && floor > 0 ? { fair, floor } : null;
}
